import { useEffect, useRef, useState } from "react";

export default function StepsSection() {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.2 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={ref} className="bg-[#F0EBFF] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        {/* Header */}
        <div className={`text-center mb-16 transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="inline-flex items-center gap-2 text-[#7C55CC] text-xs font-semibold uppercase tracking-widest mb-4">
            <i className="ri-route-line"></i>
            How It Works
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
            Three steps to{" "}
            <span className="text-[#9B72FF]">real connection</span>
          </h2>
        </div>

        {/* Cards */}
        <div className={`grid grid-cols-1 md:grid-cols-3 gap-5 transition-all duration-700 delay-200 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          {/* Step 1 */}
          <div className="bg-[#2D1B69] text-white rounded-3xl p-8 md:p-10 flex flex-col justify-between min-h-[280px] hover:-translate-y-1 transition-transform duration-300 cursor-default">
            <div>
              <span className="text-[80px] font-thin leading-none text-white/20">01</span>
              <h3 className="text-2xl font-bold mt-2 mb-3">Explore</h3>
              <p className="text-white/70 text-sm leading-relaxed">
                Browse interest-based communities, discover events, and find people who share your passions.
              </p>
            </div>
            <div className="mt-6">
              <span className="inline-block bg-[#C8B6FF]/20 text-[#C8B6FF] text-xs font-medium px-4 py-1.5 rounded-full">
                Discover interests
              </span>
            </div>
          </div>

          {/* Step 2 */}
          <div className="bg-white/70 backdrop-blur-sm rounded-3xl p-8 md:p-10 flex flex-col justify-between min-h-[280px] border border-[#E6D9FF] hover:-translate-y-1 transition-transform duration-300 cursor-default">
            <div>
              <span className="text-[80px] font-thin leading-none text-[#C8B6FF]">02</span>
              <h3 className="text-2xl font-bold mt-2 mb-3 text-gray-900">Connect</h3>
              <p className="text-gray-500 text-sm leading-relaxed">
                Like profiles without pressure. Start conversations when it feels right — naturally.
              </p>
            </div>
            <div className="mt-6">
              <span className="inline-block bg-[#E6D9FF] text-[#7C55CC] text-xs font-medium px-4 py-1.5 rounded-full">
                No awkward openers
              </span>
            </div>
          </div>

          {/* Step 3 */}
          <div className="bg-[#F5EEFF] rounded-3xl p-8 md:p-10 flex flex-col justify-between min-h-[280px] border border-[#E6D9FF] hover:-translate-y-1 transition-transform duration-300 cursor-default">
            <div>
              <span className="text-[80px] font-thin leading-none text-[#C8B6FF]">03</span>
              <h3 className="text-2xl font-bold mt-2 mb-3 text-gray-900">Meet IRL</h3>
              <p className="text-gray-500 text-sm leading-relaxed">
                Join curated events or create your own. Less texting, more real moments in real life.
              </p>
            </div>
            <div className="mt-6">
              <span className="inline-block bg-[#C8B6FF]/30 text-[#7C55CC] text-xs font-medium px-4 py-1.5 rounded-full">
                Real-life meetings
              </span>
            </div>
          </div>
        </div>

        {/* Tagline */}
        <p className={`text-center text-gray-400 font-medium mt-10 text-sm tracking-wide transition-all duration-700 delay-400 ${visible ? "opacity-100" : "opacity-0"}`}>
          No games. Just real.
        </p>
      </div>
    </section>
  );
}
